import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Download, HeartHandshake, Sparkles, Gift } from "lucide-react";
import { api, img, plural } from "../lib/api";
import { Facebook, Instagram } from "lucide-react";
import { useSite } from "../lib/store";
import { ProductCard, SectionTitle, Dots } from "../components/Bits";
import NewsletterBox from "../components/NewsletterBox";
import { useSeo, orgLd } from "../components/Seo";

const RINGS = ["var(--pink)", "var(--teal)", "var(--sun)", "var(--lilac)"];

function HeroSlider({ slides, texts }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setI((x) => (x + 1) % slides.length), 6500);
    return () => clearInterval(t);
  }, [slides.length]);
  const s = slides[i] || { title: texts.hero_title, subtitle: texts.hero_text, image: texts.hero_image };
  return (
    <section className="relative overflow-hidden paper" data-testid="hero-section">
      <Dots />
      <div className="max-w-7xl mx-auto px-5 sm:px-6 pt-6 pb-14 lg:pt-20 lg:pb-28 grid lg:grid-cols-[1.1fr_1fr] gap-5 lg:gap-12 items-center">
        <AnimatePresence mode="wait">
          <motion.div key={i} className="text-center lg:text-left" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.5 }}>
            <p className="eyebrow mb-2 lg:mb-4">{s.badge || "Pro děti v mateřských školách"}</p>
            <h1 className="text-[2rem] sm:text-5xl lg:text-6xl font-semibold leading-[1.05]" data-testid="hero-title">{s.title}</h1>
            <p className="mt-3 lg:mt-6 text-sm md:text-lg text-[var(--muted)] max-w-xl mx-auto lg:mx-0 leading-relaxed">{s.subtitle}</p>
            <div className="flex gap-2 sm:gap-3 mt-5 lg:mt-8 justify-center lg:justify-start">
              <Link to={s.button_link || "/eshop"} className="btn-pink !px-4 sm:!px-6" data-testid="hero-cta-btn">{s.button_text || "Prozkoumat materiály"} <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="flex flex-wrap justify-center lg:justify-start gap-x-4 gap-y-2 lg:gap-6 mt-5 lg:mt-10 text-xs lg:text-sm">
              {[[Download, "Okamžité stažení"], [HeartHandshake, "Tvořeno s láskou pro MŠ"], [Sparkles, "Tisk doma i ve školce"]].map(([I, t]) => (
                <span key={t} className="flex items-center gap-1.5 lg:gap-2 font-semibold"><span className="w-7 h-7 lg:w-9 lg:h-9 rounded-full bg-[var(--teal-soft)] grid place-items-center"><I className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-[var(--teal)]" /></span>{t}</span>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
        <div className="relative mx-auto w-full max-w-[220px] sm:max-w-[340px] lg:max-w-[480px] aspect-square order-first lg:order-none">
          <svg viewBox="0 0 200 200" className="absolute -inset-4 w-[calc(100%+2rem)] h-[calc(100%+2rem)]" aria-hidden>
            <path d="M100 6 C 160 4, 196 50, 194 104 C 192 160, 150 196, 96 194 C 42 192, 4 150, 8 96 C 12 44, 48 8, 100 6 Z" fill="none" stroke="var(--pink)" strokeWidth="1.4" strokeDasharray="4 5" />
          </svg>
          <AnimatePresence mode="wait">
            <motion.img key={s.image} src={img(s.image)} alt={s.title} initial={{ opacity: 0, scale: 0.94, rotate: -3 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} exit={{ opacity: 0, scale: 1.03 }} transition={{ duration: 0.6 }}
              className="w-full h-full object-cover rounded-full border-[6px] lg:border-[10px] border-white shadow-[0_30px_60px_-25px_rgba(212,86,140,.45)]" data-testid="hero-image" />
          </AnimatePresence>
          <span className="absolute -bottom-3 -left-10 lg:-bottom-6 lg:-left-8 w-24 h-24 sm:w-32 sm:h-32 lg:w-48 lg:h-48 bg-white rounded-full shadow-lg floaty grid place-items-center" data-testid="hero-logo-bubble"><img src="/logo.png" alt="" className="w-[78%] h-[78%] object-contain" /></span>
          <span className="absolute top-1 -right-10 lg:top-4 lg:-right-2 font-display text-xs lg:text-base bg-[var(--teal-soft)] text-[#2f8f8a] px-3 lg:px-4 py-1 lg:py-1.5 rounded-full rotate-6 shadow" data-testid="hero-love-badge">pro děti od 2–7 let</span>
        </div>
      </div>
      {slides.length > 1 && (
        <div className="absolute bottom-4 lg:bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
          {slides.map((_, k) => (
            <button key={k} onClick={() => setI(k)} data-testid={`hero-dot-${k}`} aria-label={`Slide ${k + 1}`} className={`h-2.5 rounded-full transition-[width,background-color] duration-300 ${k === i ? "w-8 bg-[var(--pink)]" : "w-2.5 bg-[#ead3de]"}`} />
          ))}
        </div>
      )}
    </section>
  );
}

function CategoryBubbles({ categories, title }) {
  const cats = categories.filter((c) => c.show_on_home);
  const sizes = ["w-36 h-36 sm:w-56 sm:h-56", "w-24 h-24 sm:w-32 sm:h-32", "w-32 h-32 sm:w-44 sm:h-44", "w-28 h-28 sm:w-36 sm:h-36", "w-32 h-32 sm:w-48 sm:h-48", "w-24 h-24 sm:w-28 sm:h-28", "w-28 h-28 sm:w-40 sm:h-40"];
  const offs = ["translate-y-2 sm:translate-y-6", "-translate-y-2 sm:-translate-y-10", "translate-y-4 sm:translate-y-16", "sm:-translate-y-2", "-translate-y-3 sm:translate-y-10", "translate-y-3 sm:-translate-y-14", "sm:translate-y-4", "translate-y-1 sm:-translate-y-6"];
  const tilt = ["-rotate-3", "rotate-2", "rotate-6", "-rotate-6", "rotate-1", "-rotate-2", "rotate-3"];
  const gaps = ["sm:mx-3", "sm:mx-8", "sm:mx-5", "sm:mx-10", "sm:mx-2"];
  return (
    <section className="relative bg-[var(--powder)] overflow-hidden" data-testid="categories-section">
      <Dots />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="text-center mb-14">
          <p className="eyebrow mb-2">Kategorie</p>
          <h2 className="text-3xl sm:text-4xl font-semibold"><span className="squiggle">{title}</span></h2>
        </div>
        <div className="flex flex-wrap justify-center items-center gap-x-3 gap-y-8 sm:gap-y-6 pb-12">
          {cats.map((c, i) => (
            <motion.div key={c.id} initial={{ opacity: 0, scale: 0.6 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.05, type: "spring", stiffness: 140 }} className={`transform ${offs[i % offs.length]} ${gaps[i % gaps.length]}`}>
              <Link to={`/kategorie/${c.slug}`} data-testid={`category-bubble-${c.slug}`} className={`group flex flex-col items-center text-center ${tilt[i % tilt.length]} hover:rotate-0 transition-transform duration-300`}>
                <span className={`relative ${sizes[i % sizes.length]} rounded-full p-1.5 bg-white shadow-[0_18px_40px_-22px_rgba(185,66,111,.55)] transition-transform duration-300 group-hover:scale-110`}>
                  <span className="absolute -inset-2 rounded-full border-2 border-dashed opacity-80" style={{ borderColor: RINGS[i % 4] }} />
                  <img src={img(c.image)} alt={c.name} loading="lazy" className="w-full h-full object-cover rounded-full" />
                </span>
                <span className="mt-3 max-w-[10rem] font-display text-base sm:text-lg leading-tight group-hover:text-[var(--pink-dark)] transition-colors">{c.name}</span>
                {c.count > 0 && <span className="text-xs text-[var(--muted)]">{plural(c.count, "materiál", "materiály", "materiálů")}</span>}
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Why({ texts }) {
  return (
    <section className="bg-[var(--powder)]" data-testid="why-section">
      <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-[1fr_1.3fr] gap-14 items-center relative overflow-hidden">
        <Dots />
        <div className="relative">
          <img src={img(texts.about_image) || "/logo.png"} alt="" className="w-full max-w-md mx-auto rounded-[40px] border-8 border-white shadow-[0_30px_60px_-30px_rgba(212,86,140,.45)] -rotate-2" />
          <p className="font-display text-xl text-center text-[var(--pink-dark)] mt-3">s láskou, Aneta</p>
        </div>
        <div className="relative">
          <p className="eyebrow mb-2">Náš příběh</p>
          <h2 className="text-3xl sm:text-4xl font-semibold mb-6"><span className="squiggle">{texts.why_title}</span></h2>
          <div className="space-y-4 text-[#4a4752] leading-relaxed whitespace-pre-line" data-testid="why-text">{texts.why_text}</div>
          <Link to="/o-nas" className="btn-ghost mt-8" data-testid="why-more-btn">Více o nás <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { site } = useSite();
  const [news, setNews] = useState([]);
  useSeo({ jsonld: orgLd });
  useEffect(() => {
    api.get("/products?is_new=true&sort=new").then((r) => setNews(r.data));
  }, []);
  if (!site) return <div className="h-[70vh]" />;
  const t = site.texts;
  return (
    <div>
      <HeroSlider slides={site.slides} texts={t} />
      <CategoryBubbles categories={site.categories} title={t.categories_title} />
      {news.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-16" data-testid="news-section">
          <SectionTitle eyebrow="Právě přidáno" title={t.news_title || "Novinky"} right={<Link to="/eshop?novinky=1&sort=new" className="hidden sm:flex items-center gap-1 font-bold text-[var(--pink-dark)] hover:gap-2 transition-[gap]" data-testid="news-all-link">Všechny novinky <ArrowRight className="w-4 h-4" /></Link>} />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {news.slice(0, 8).map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}
          </div>
        </section>
      )}
      <Why texts={t} />
      <section className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-2 gap-6">
        <NewsletterBox title={t.newsletter_title} text={t.newsletter_text} />
        <Link to="/darkove-poukazy" data-testid="home-vouchers-teaser" className="group relative overflow-hidden rounded-[32px] bg-[var(--sun-soft)] p-8 sm:p-10 flex flex-col justify-between min-h-[260px]">
          <Dots />
          <div className="relative">
            <p className="eyebrow mb-2">Dárkové poukazy</p>
            <h3 className="text-3xl font-semibold">Darujte radost z tvoření</h3>
            <p className="mt-3 text-[var(--muted)] max-w-md">{t.vouchers_text}</p>
          </div>
          <span className="relative btn-pink self-start mt-6">Vybrat poukaz <Gift className="w-4 h-4 group-hover:rotate-12 transition-transform" /></span>
        </Link>
      </section>
      <Social social={site.social} />
    </div>
  );
}

function Social({ social = {} }) {
  const items = [["facebook", Facebook, "Facebook", social.facebook_text, "#E8F0FE", "#3b5bdb"], ["instagram", Instagram, "Instagram", social.instagram_text, "var(--pink-soft)", "var(--pink-dark)"]].filter(([k]) => social[k]);
  if (!items.length) return null;
  return (
    <section className={`max-w-7xl mx-auto px-6 py-10 grid ${items.length > 1 ? "md:grid-cols-2" : ""} gap-6`} data-testid="social-banners">
      {items.map(([k, I, l, t, bg, fg]) => (
        <a key={k} href={social[k]} target="_blank" rel="noreferrer" data-testid={`social-banner-${k}`} className="group relative overflow-hidden rounded-[32px] p-8 flex items-center gap-6 hover:-translate-y-1 transition-transform" style={{ background: bg }}>
          <Dots />
          <span className="relative w-20 h-20 shrink-0 rounded-full bg-white grid place-items-center shadow group-hover:rotate-6 transition-transform"><I className="w-9 h-9" style={{ color: fg }} /></span>
          <div className="relative"><p className="font-display text-xs tracking-[0.2em] text-[var(--teal)] mb-1">SLEDUJTE NÁS</p><p className="font-display text-2xl">{l}</p><p className="text-[var(--muted)] font-semibold">{t}</p></div>
          <ArrowRight className="relative ml-auto w-6 h-6 group-hover:translate-x-1 transition-transform" style={{ color: fg }} />
        </a>
      ))}
    </section>
  );
}
